import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Drawer,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { LOCAL_STORAGE_KEYS } from "../../../services/local_storage_keys";

interface TabConfig {
  label: string;
  content: React.ReactNode;
  icon?: React.ReactElement;
}

interface ConsultingTabsProps {
  tabs: TabConfig[];
  storageKey?: string; // אופציונלי - מאפשר שימוש ב-key שונה
  onTabChange?: (index: number) => void;
}

export const ResponsiveConsultingTabs: React.FC<ConsultingTabsProps> = ({
  tabs,
  storageKey = LOCAL_STORAGE_KEYS.consultingTabsSelectedIndex,
  onTabChange,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const storedTab = Number(localStorage.getItem(storageKey)) || 0;
  const [selectedTab, setSelectedTab] = useState(storedTab);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, selectedTab.toString());
  }, [selectedTab, storageKey]);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    onTabChange?.(index);
    contentRef.current?.scrollTo({ top: 0 });
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* כפתור עם שם הטאב הנבחר (רק במסך קטן) */}
      {isSmallScreen && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", p: 1 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDrawerOpen(true)}
            size="small"
            sx={{
              borderRadius: "20px",
              border: "1px solid #1976d2",
              padding: "6px 16px",
              fontSize: "14px",
              textTransform: "none",
              direction: "rtl",
               backgroundColor: "#1976d2",
              color: "white",
            }}
          >
            {tabs[selectedTab]?.label}
            {tabs[selectedTab]?.icon && (
              <Box component="span" sx={{ ml: 1 }}>
                {tabs[selectedTab].icon}
              </Box>
            )}
          </Button>
        </Box>
      )}

      {/* Tabs רגילים במסך גדול */}
      {!isSmallScreen && (
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => handleTabChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexShrink: 0,
            "& .MuiTabs-flexContainer": { flexWrap: "nowrap" },
            "& .MuiTab-root": {
              borderRadius: "20px",
              border: "1px solid #1976d2",
              margin: "5px",
              padding: "6px 16px",
              minHeight: "48px",
              fontSize: "16px",
              textTransform: "none",
              flexDirection: "row",
              gap: "8px",
            },
            "& .Mui-selected": {
              backgroundColor: "#1976d2",
              color: "white",
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="end"
            />
          ))}
        </Tabs>
      )}

      {/* Drawer מימין למסך מובייל */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 250, p: 2, direction: "rtl", mt: 8 }}>
          {tabs.map((tab, index) => (
            <Button
              key={index}
              fullWidth
              variant="text"
              onClick={() => {
                handleTabChange(index);
                setDrawerOpen(false);
              }}
                    sx={{
                justifyContent: "flex-start",
                mb: 1,
                textAlign: "right",
                borderRadius: "20px",
                border: "1px solid #1976d2",
                backgroundColor: selectedTab === index ? "#1976d2" : "transparent",
                color: selectedTab === index ? "white" : "black",
                "&:hover": {
                    backgroundColor: selectedTab === index ? "#1565c0" : "#f0f0f0",
                },
                }}
            >
              {tab.label}
              {tab.icon && (
                <Box component="span" sx={{ ml: 1 }}>
                  {tab.icon}
                </Box>
              )}
            </Button>
          ))}
        </Box>
      </Drawer>

      {/* תוכן הטאב */}
      <Box
        ref={contentRef}
        sx={{
          padding: "5px",
          flexGrow: 1,
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {tabs[selectedTab]?.content || <CircularProgress />}
      </Box>
    </Box>
  );
};
